import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import { Box, Divider, FormHelperText, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import EditIcon from '@mui/icons-material/Edit';
import _ from 'lodash';

import useNotification from '../../../../../../core/hooks/use_notification';
import { Button, SubmitButton } from '../../../../../common/button';
import TextFieldFormik from '../../../../../common/text_field';
import { updateProductQuantityLimitAction, updateProductQuantityLimitState } from '../../../../../../features/reservation/update_prduct_quantity_limit';
import { updateProductQuantityLimitNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { getAllPopsState } from "../../../../../../features/pop/get_all_pops";

const UpdateProductQuantityLimitDialog = ({ product, date, refreshCalendar }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const updateProductQuantityLimit = useSelector(updateProductQuantityLimitState);
    const getAllPops = useSelector(getAllPopsState);

    const [open, setOpen] = useState(false);

    const onClose = () => setOpen(false);

    const initialValues = {
        newGeneralQuantityLimit: product?.generalQuantityLimit || '',
        pops: getAllPops?.data?.map(pop => ({
            id: pop?.id,
            quantityLimit: product?.popsQuantityLimits[pop?.id] || ''
        }))
    };

    const schema = Yup.object().shape({
        newGeneralQuantityLimit: Yup.number().required(),
        pops: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                quantityLimit: Yup.number().notRequired()
            })
        )
    });

    const onSubmit = (values, { setErrors }) => {
        const errors = validate(values);

        if (errors.pops) {
            setErrors(errors);
            return;
        }

        const newPopsQuantityLimits = values?.pops
            ?.filter((popQuantityLimit) => !!popQuantityLimit.quantityLimit)
            ?.reduce((accumulator, current) => {
                accumulator[current.id] = current.quantityLimit;
                return accumulator;
            }, {});

        const form = {
            ..._.omit(values, ['pops']),
            newPopsQuantityLimits,
            date,
            productId: product?.productId
        };

        dispatch(updateProductQuantityLimitAction(form))
            .then(response => {
                showNotification(updateProductQuantityLimitNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshCalendar();
            });
    };

    const validate = (values) => {
        const errors = { pops: '' };

        const popsQuantity = values?.pops
            ?.map(pop => pop?.quantityLimit)
            ?.reduce((accumulator, current) => accumulator + current, 0);
        const generalQuantityLimit = values?.newGeneralQuantityLimit;

        if (popsQuantity > generalQuantityLimit) {
            errors.pops = `Przydzielono popom ${ popsQuantity } szt., gdy globalny limit wynosi: ${ generalQuantityLimit } szt.`;
        }

        return errors;
    };

    return (
        <>
            <Tooltip title="Edytuj limit">
                <IconButton size="small" color="primary" onClick={ () => setOpen(true) }>
                    <EditIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog disableRestoreFocus PaperProps={ { sx: { maxHeight: 800 } } } open={ open } onClose={ onClose }
                    fullWidth maxWidth="lg">
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Edytuj limit ({ product?.productName })</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    <Formik
                        initialValues={ initialValues }
                        validationSchema={ schema }
                        validateOnChange={ false }
                        validateOnBlur={ false }
                        onSubmit={ onSubmit }>
                        { () => (
                            <Form>
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 } sm={ 6 }>
                                        <TextFieldFormik
                                            name="newGeneralQuantityLimit"
                                            type="number"
                                            label="Globalny limit rezerwacji"
                                            inputProps={ { min: '1', step: '1' } }
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={ 12 }>
                                        <TableContainer component={ Paper } variant="outlined">
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow sx={ {
                                                        '& th': {
                                                            backgroundColor: 'slate.50',
                                                            fontWeight: 600
                                                        }
                                                    } }>
                                                        <TableCell>Punkt odbioru</TableCell>
                                                        <TableCell align="center">Liczba zarezerwowanych
                                                            szt.</TableCell>
                                                        <TableCell align="center">Limit rezerwacji</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <FieldArray name="pops">
                                                        { () => (
                                                            getAllPops?.data?.map((pop, index) => (
                                                                <TableRow key={ pop?.id } hover>
                                                                    <TableCell>
                                                                        { pop?.hubName } / { pop?.name }
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        { product?.popsQuantityReservations[pop?.id] || 0 }
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <TextFieldFormik
                                                                            name={ `pops.${ index }.quantityLimit` }
                                                                            size="small"
                                                                            type="number"
                                                                            inputProps={ { min: '0', step: '1' } }
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        ) }
                                                    </FieldArray>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>

                                <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                                    <FormHelperText error>
                                        <ErrorMessage name="pops"/>
                                    </FormHelperText>
                                </Box>

                                <Divider sx={ { mt: 3, mb: 2, mx: -3 } }/>

                                <DialogActions sx={ { p: 0, gap: 1 } }>
                                    <Button variant="outlined" onClick={ onClose }>Zamknij</Button>
                                    <SubmitButton
                                        isLoading={ updateProductQuantityLimit?.loading }>Zapisz</SubmitButton>
                                </DialogActions>
                            </Form>
                        ) }
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default UpdateProductQuantityLimitDialog;